/**
 * Lightbox - 전체 화면 이미지 뷰어 컴포넌트
 *
 * Portal로 렌더링되며 키보드(좌우 화살표, Esc) 및 터치 스와이프 내비게이션을 지원.
 * 인증 이미지를 Blob으로 가져오고, 인접 이미지를 미리 로드하여 UX를 개선한다.
 * @param images - 이미지 목록 (url, alt, caption)
 * @param initialIndex - 초기 표시할 이미지 인덱스
 */
'use client';

import React, { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { fetchAuthImage } from '@/lib/fetchAuthImage';
import styles from './Lightbox.module.css';

export interface LightboxImage {
  url: string;
  alt: string;
  caption?: string;
}

export interface LightboxProps {
  images: LightboxImage[];
  initialIndex: number;
  onClose: () => void;
}

export default function Lightbox({ images, initialIndex, onClose }: LightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [blobCache, setBlobCache] = useState<Record<string, string>>({});
  const [loadingUrls, setLoadingUrls] = useState<Set<string>>(new Set());

  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const total = images.length;
  const current = images[currentIndex];

  const goTo = useCallback(
    (index: number) => {
      if (index >= 0 && index < total) setCurrentIndex(index);
    },
    [total],
  );

  const goPrev = useCallback(() => goTo(currentIndex - 1), [currentIndex, goTo]);
  const goNext = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);

  // --- Focus management: trap focus, save/restore ---
  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    return () => {
      document.body.style.overflow = '';
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  // --- Focus trap ---
  const handleTabKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;

    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button:not([disabled]):not([aria-disabled="true"]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  // --- Keyboard navigation ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'ArrowRight':
          setCurrentIndex((prev) => (prev < total - 1 ? prev + 1 : prev));
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleTabKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleTabKeyDown);
    };
  }, [total, onClose, handleTabKeyDown]);

  // --- Fetch current image + prefetch adjacent ---
  useEffect(() => {
    const urlsToLoad = [
      current.url,
      currentIndex > 0 ? images[currentIndex - 1].url : null,
      currentIndex < total - 1 ? images[currentIndex + 1].url : null,
    ].filter((u): u is string => u !== null && !blobCache[u]);

    if (urlsToLoad.length === 0) return;

    const controller = new AbortController();

    setLoadingUrls((prev) => {
      const next = new Set(prev);
      urlsToLoad.forEach((u) => next.add(u));
      return next;
    });

    urlsToLoad.forEach(async (url) => {
      const blob = await fetchAuthImage(url, controller.signal);
      if (controller.signal.aborted) {
        if (blob) URL.revokeObjectURL(blob);
        return;
      }
      if (blob) {
        setBlobCache((prev) => ({ ...prev, [url]: blob }));
      }
      setLoadingUrls((prev) => {
        const next = new Set(prev);
        next.delete(url);
        return next;
      });
    });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- blobCache is intentionally excluded to avoid re-fetching on every cache update
  }, [currentIndex, total]);

  // --- Cleanup all blob URLs on unmount ---
  useEffect(() => {
    return () => {
      Object.values(blobCache).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup runs only on unmount; blobCache ref is stable
  }, []);

  // --- Touch/swipe handling ---
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return;
      const touch = e.changedTouches[0];
      const dx = touch.clientX - touchStartRef.current.x;
      const dy = touch.clientY - touchStartRef.current.y;
      touchStartRef.current = null;

      // Only trigger if horizontal swipe > 50px and more horizontal than vertical
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) goNext();
        else goPrev();
      }
    },
    [goNext, goPrev],
  );

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isLoading = !blobCache[current.url] && loadingUrls.has(current.url);
  const blobUrl = blobCache[current.url] || null;

  const lightbox = (
    <div
      ref={dialogRef}
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label="이미지 뷰어"
      tabIndex={-1}
      onClick={handleBackdropClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close */}
      <button
        ref={closeButtonRef}
        className={styles.closeButton}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="닫기"
      >
        <X size={24} />
      </button>

      {/* Prev */}
      <button
        className={`${styles.navButton} ${styles.navPrev}`}
        onClick={(e) => {
          e.stopPropagation();
          goPrev();
        }}
        aria-label="이전 이미지"
        aria-disabled={currentIndex === 0}
      >
        <ChevronLeft size={28} />
      </button>

      {/* Content */}
      <div className={styles.content} onClick={(e) => e.stopPropagation()}>
        <div className={styles.imageWrapper}>
          {isLoading && <div className={styles.spinner} />}
          {blobUrl && (
            // eslint-disable-next-line @next/next/no-img-element -- blob URL from fetchAuthImage cannot be optimized by next/image
            <img src={blobUrl} alt={current.alt} className={styles.image} />
          )}
          {!isLoading && !blobUrl && <div className={styles.spinner} />}
        </div>
        <div className={styles.caption}>
          {current.caption && <span>{current.caption}</span>}
          <span className={styles.counter} aria-live="polite" aria-atomic="true">
            {currentIndex + 1} / {total}
          </span>
        </div>
      </div>

      {/* Next */}
      <button
        className={`${styles.navButton} ${styles.navNext}`}
        onClick={(e) => {
          e.stopPropagation();
          goNext();
        }}
        aria-label="다음 이미지"
        aria-disabled={currentIndex === total - 1}
      >
        <ChevronRight size={28} />
      </button>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(lightbox, document.body);
  }
  return null;
}
